import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as echarts from 'echarts';
import { LpiAggregateBucket } from './types';
import { useEchart } from './useEchart';
import ChartErrorState from '../cpiReports/ChartErrorState';
import { formatNumber } from '../cpiReports/types';
import { chartPalette, chartChrome } from '../../utils/chartColors';
import './LpiBarChart.scss';

/**
 * Which figure the bars carry. 'lpi' is a ratio with no natural ceiling;
 * 'benuttingsgraad' is a percentage, so it gets a 0-100 axis and a % suffix.
 */
type LpiChartMetric = 'lpi' | 'benuttingsgraad';

interface LpiBarChartProps {
  titleKey: string;
  buckets: LpiAggregateBucket[] | null;
  categories?: string[];
  isLoading: boolean;
  error?: Error | null;
  onRetry?: () => void;
  /** Defaults to 'lpi', so existing chart instances are unaffected. */
  metric?: LpiChartMetric;
  /**
   * Drill-through. When omitted the chart is inert and its bars do not take a
   * pointer cursor — a chart that looks clickable but isn't reads as broken.
   */
  onBucketClick?: (bucket: string) => void;
}

const LpiBarChart: FC<LpiBarChartProps> = ({
  titleKey,
  buckets,
  categories,
  isLoading,
  error,
  onRetry,
  metric = 'lpi',
  onBucketClick,
}) => {
  const { t } = useTranslation();
  const isClickable = onBucketClick != null;

  const option = useMemo<echarts.EChartsOption | null>(() => {
    if (!buckets || buckets.length === 0) return null;

    const byBucket = new Map(buckets.map((b) => [b.bucket, b]));
    const isPercent = metric === 'benuttingsgraad';
    // `?? null` collapses both "field absent" (older backend) and "null"
    // (no order carried a capacity) into a gap. Never 0 — that would read as
    // "measured, and empty".
    const valueOf = (b: LpiAggregateBucket | undefined): number | null => {
      if (b == null) return null;
      if (isPercent) return b.benuttingsgraad ?? null;
      return b.lpi;
    };

    // Union, not override: keep the continuous range so sparse months stay
    // visible as gaps, but never silently drop a bucket the backend
    // returned. `categories` can be `[]` (monthRange('', '') — the default
    // and post-clear filter state) which is not nullish, so `??` alone
    // would discard every bucket; guard on length instead.
    // No Set spread here: tsconfig targets es5 without downlevelIteration, so
    // iterating a Set is a compile error. concat + indexOf gives the same union.
    //
    // Only the categorical charts (by-vehicle / by-client / by-order) are
    // sorted by value. A supplied `categories` axis is a continuous month
    // range, where position carries meaning — sorting it low-to-high would
    // turn a timeline into a ranking. Hence the sort lives in this branch
    // alone, never in the one above it.
    const axis =
      categories && categories.length > 0
        ? categories
            .concat(
              buckets
                .map((b) => b.bucket)
                .filter((k) => categories.indexOf(k) === -1)
            )
            .sort()
        : buckets
            // Copy first: `buckets` is the caller's array (straight off the
            // aggregates hook's state) and sort mutates in place.
            .slice()
            .sort((a, b) => {
              const av = valueOf(a);
              const bv = valueOf(b);
              // Unmeasured buckets trail the measured ones. Treating null as
              // 0 would plant every gap at the low end of the axis, reading
              // as "worst performer" rather than "no data".
              if (av === null) return bv === null ? 0 : 1;
              if (bv === null) return -1;
              return av - bv;
            })
            .map((b) => b.bucket);

    const data = axis.map((c) => valueOf(byBucket.get(c)));

    const palette = chartPalette();
    const chrome = chartChrome();

    return {
      aria: { enabled: true },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const p = Array.isArray(params) ? params[0] : params;
          const b = byBucket.get(p.name);

          const value = valueOf(b);
          // `!b` is redundant at runtime — valueOf(undefined) is already null —
          // but it is what narrows `b` for the property reads below.
          if (!b || value === null) {
            return `${p.name}<br/>${t('lpi.chart.noData')}`;
          }

          return [
            p.name,
            `${p.marker} ${
              isPercent ? `${value.toFixed(2)}%` : value.toFixed(3)
            }`,
            `${t('lpi.chart.tooltip.orders')}: ${b.orders}`,
            `${t('lpi.chart.tooltip.tonKm')}: ${formatNumber(b.total_belad)}`,
          ].join('<br/>');
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        top: '10%',
        bottom: '5%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: axis,
        axisLabel: { fontSize: 10, color: chrome.muted, rotate: 30 },
      },
      yAxis: {
        type: 'value',
        // Benuttingsgraad is bounded at 100%, so pin the axis: an autoscaled
        // axis would make a good month and a bad month look alike.
        max: isPercent ? 100 : undefined,
        axisLabel: {
          color: chrome.muted,
          formatter: isPercent
            ? (value: number) => `${value}%`
            : undefined,
        },
        splitLine: { lineStyle: { type: 'dotted', color: chrome.grid } },
      },
      series: [
        {
          type: 'bar',
          data,
          itemStyle: { color: palette[0] },
          barMaxWidth: 40,
          // ECharts defaults every bar to a pointer cursor whether or not
          // anything is listening, so an unwired chart has to opt out
          // explicitly.
          cursor: isClickable ? 'pointer' : 'default',
        },
      ],
    };
  }, [buckets, categories, metric, t, isClickable]);

  const chartRef = useEchart(option, onBucketClick);

  return (
    <div className="lpi-bar-chart" data-testid="lpi-bar-chart-card">
      <h3 className="lpi-bar-chart__title">{t(titleKey)}</h3>

      {isLoading && (
        <div
          className="lpi-bar-chart__skeleton"
          data-testid="lpi-bar-chart-skeleton"
        />
      )}

      {error && !isLoading && <ChartErrorState onRetry={onRetry} />}

      {!error && !isLoading && buckets && buckets.length === 0 && (
        <p className="lpi-bar-chart__empty">{t('lpi.chart.noData')}</p>
      )}

      {!error && !isLoading && option && (
        <div
          className="lpi-bar-chart__canvas"
          data-testid="lpi-bar-chart"
          ref={chartRef}
          aria-label={t(titleKey)}
        />
      )}
    </div>
  );
};

export default LpiBarChart;
